import { Field as FormikField, FieldProps, FormikValues } from 'formik';

import useRendererFunctions from '../hooks/useRendererFunctions';
import { FormSlowCustomField, RendererParams } from '../types';

interface CustomTypeRendererParams<
  TValue extends any = any,
  TValues extends FormikValues = FormikValues,
> {
  fieldName: string;
  fieldKey?: string;
  Component: FormSlowCustomField<TValue, TValues>['Component'];
  field?: FormSlowCustomField<TValue, TValues>;
}

type RendererType<TValue extends any = any, TValues extends FormikValues = FormikValues> = {
  status?: any;
  Component: React.FC<RendererParams<TValue, TValues>>;
  value: Omit<RendererParams<TValue, TValues>, 'onChange' | 'setErrorElement' | 'setNudgeState'>;
  field?: FormSlowCustomField<TValue, TValues>;
};

const Renderer = <TValue extends any = any, TValues extends FormikValues = FormikValues>({
  Component,
  value,
  status,
  field,
}: RendererType<TValue, TValues>) => {
  const { setFieldValue, setStatus, ...rest } = value;
  const { onChange, setErrorElement, setNudgeState } = useRendererFunctions({
    name: rest.name,
    setFieldValue,
    setStatus,
    status,
  });

  return Component({
    ...rest,
    setStatus,
    onChange,
    setFieldValue,
    setErrorElement,
    setNudgeState,
    field,
  });
};

export const customTypeRenderer = <
  TValue extends any = any,
  TValues extends FormikValues = FormikValues,
>({
  fieldName,
  Component,
  field,
}: CustomTypeRendererParams<TValue, TValues>) => {
  if (!Component) {
    return null;
  }

  return (
    <FormikField name={fieldName}>
      {({ field: { value, name }, meta: { error, touched }, form }: FieldProps) => {
        const {
          errors,
          values,
          status,
          setFieldValue,
          handleChange,
          setFieldError,
          setFieldTouched,
          handleBlur,
          setStatus,
        } = form;

        return (
          <Renderer
            Component={Component}
            field={field}
            status={status}
            value={{
              formik: form,
              value,
              values,
              error,
              errors,
              touched,
              name,
              setStatus,
              handleBlur,
              handleChange,
              setFieldValue,
              setFieldError,
              setFieldTouched,
            }}
          />
        );
      }}
    </FormikField>
  );
};
